import { useEffect, useRef, useState } from "react";

import type { CollectionKind, ReportStatus } from "@/types/api";

const RATE_PER_IMAGE_S = 0.33;
const DEFAULT_DURATION_S = 60;
const MIN_DURATION_S = 10;
const MAX_PROGRESS = 90;
const TICK_INTERVAL_MS = 500;
const COMPLETION_ANIM_MS = 800;
const STORAGE_PREFIX = "report-progress-start:";

type Phase = { label: string; start: number };

const PHOTO_PHASES: readonly Phase[] = [
  { label: "Organising your photos…", start: 0 },
  { label: "Reviewing site documentation…", start: 10 },
  { label: "Building report structure…", start: 22 },
  { label: "Laying out pages…", start: 38 },
  { label: "Adding photo details…", start: 52 },
  { label: "Applying template formatting…", start: 65 },
  { label: "Running final checks…", start: 78 },
  { label: "Finalising report…", start: 85 },
];

// Same thresholds, issues wording: a punch-list export must never claim to be
// "Organising your photos…". See [[report-progress-bar.spec]] § Phase labels.
const ISSUE_PHASES: readonly Phase[] = [
  { label: "Gathering your issues…", start: 0 },
  { label: "Reading the latest updates…", start: 10 },
  { label: "Building report structure…", start: 22 },
  { label: "Laying out pages…", start: 38 },
  { label: "Placing issues on plans…", start: 52 },
  { label: "Adding issue photos…", start: 65 },
  { label: "Running final checks…", start: 78 },
  { label: "Finalising report…", start: 85 },
];

export interface ReportProgress {
  /** Current simulated progress (0–100). Caps at 90 until completion. */
  progress: number;
  /** Phase label matching the current progress threshold. */
  phaseLabel: string;
  /** True while the 90→100% completion animation is playing. */
  isCompleting: boolean;
  /** True once the simulation has finished its hand-off (terminal). */
  isDone: boolean;
}

function getEstimatedDurationS(recordCount: number | null | undefined): number {
  if (recordCount == null || recordCount <= 0) return DEFAULT_DURATION_S;
  return Math.max(MIN_DURATION_S, recordCount * RATE_PER_IMAGE_S);
}

export function getPhaseLabel(
  progress: number,
  kind: CollectionKind = "records",
): string {
  const phases = kind === "issues" ? ISSUE_PHASES : PHOTO_PHASES;
  let label = phases[0].label;
  for (const phase of phases) {
    if (progress >= phase.start) label = phase.label;
  }
  return label;
}

function getStoredStartTime(reportId: string): number | null {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + reportId);
    if (!stored) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function setStoredStartTime(reportId: string, time: number): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + reportId, String(time));
  } catch {
    // localStorage may be unavailable in private mode / quota exhausted —
    // simulation still runs in-memory; just no continuity across reloads.
  }
}

function clearStoredStartTime(reportId: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + reportId);
  } catch {
    // see setStoredStartTime — silently swallow.
  }
}

/**
 * Client-side progress simulation for an in-progress project report.
 * Mirrors otus-web's `useReportProgress` exactly — same curve, same phase
 * thresholds, same persistence key, same completion timing — so reports
 * generated in either codebase feel identical.
 *
 * See [[report-progress-bar.spec]] for the full behavior contract.
 */
export function useReportProgress(
  reportId: string,
  status: ReportStatus,
  recordCount: number | null | undefined,
  /** What the report holds; picks the phase wording. Photos by default. */
  kind: CollectionKind = "records",
): ReportProgress {
  const [progress, setProgress] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const durationS = getEstimatedDurationS(recordCount);

  const isInProgress = status === "PENDING" || status === "PROCESSING";
  const isCompleted = status === "COMPLETED";
  const isFailed = status === "FAILED";

  // Tick the simulation forward while in-progress.
  useEffect(() => {
    if (!isInProgress) return;

    if (startTimeRef.current === null) {
      // Restore from localStorage (continuity across navigation/refresh) or
      // start fresh.
      const stored = getStoredStartTime(reportId);
      const startTime = stored ?? Date.now();
      startTimeRef.current = startTime;
      if (!stored) setStoredStartTime(reportId, startTime);
    }

    const interval = setInterval(() => {
      const start = startTimeRef.current;
      if (start === null) return;
      const elapsed = (Date.now() - start) / 1000;
      const fraction = Math.min(elapsed / durationS, 1);
      const eased = 1 - Math.pow(1 - fraction, 2);
      setProgress(eased * MAX_PROGRESS);
    }, TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isInProgress, durationS, reportId]);

  // Completion handoff: animate from current value to 100% over ~600ms,
  // then settle for 200ms before marking the simulation done. The parent
  // row reads `isDone` to time the layout swap (see report-item.spec
  // "Completing" state).
  //
  // If the simulation never ran (e.g., the row mounted with status already
  // COMPLETED — the user wasn't watching this report generate), skip the
  // animation and mark done immediately. The completion animation is only
  // meaningful for in-progress→completed transitions the user can see.
  useEffect(() => {
    if (!isCompleted || isDone) return;

    if (startTimeRef.current === null) {
      clearStoredStartTime(reportId);
      // One-shot mount-time marker — the simulation hasn't run, so there's
      // nothing to animate. Same external-state-mirror exception used in
      // records-view.tsx for selection cleanup.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsDone(true);
      return;
    }

    setIsCompleting(true);
    setProgress(100);
    clearStoredStartTime(reportId);
    const timer = setTimeout(() => {
      setIsCompleting(false);
      setIsDone(true);
    }, COMPLETION_ANIM_MS);
    return () => clearTimeout(timer);
  }, [isCompleted, isDone, reportId]);

  // Failure: clear persistence, mark done immediately, no animation.
  useEffect(() => {
    if (!isFailed) return;
    clearStoredStartTime(reportId);
    // One-shot terminal-status marker mirroring an external state change;
    // there's no derived-render alternative because `isDone` is the
    // explicit signal the parent reads to time the layout swap.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsDone(true);
  }, [isFailed, reportId]);

  return {
    progress,
    phaseLabel: getPhaseLabel(progress, kind),
    isCompleting,
    isDone,
  };
}
